import { Container, Graphics, Text } from 'pixi.js';
import { DamageableEntity } from './DamageableEntity';
import { ShipDebris, createShipExplosion, createSVGExplosion } from '../ShipGraphics';
import { degToRad, radToDeg, wrapAngle } from '../../utils/angleUtils';
import { tween, delay, cancelDelay } from '../../core/TweenManager';
import gsap from 'gsap';

/**
 * ShipEntity - Base class for all ship entities (Player, RemotePlayer, BotEntity).
 *
 * Extends: DamageableEntity
 *
 * Provides:
 * - Damage indicator bar (world-positioned, doesn't rotate with ship)
 * - Name tag system
 * - Rotation interpolation
 * - Shield hit effect (arc flash)
 * - Hull hit effect (sparks)
 * - Ship explosion with debris
 *
 * Note: This is separate from DamageableEntity's showHealthBar() because ships
 * need world-positioned health bars that don't rotate with the ship.
 */
export abstract class ShipEntity extends DamageableEntity {
  // ============================================
  // Name Tag System
  // ============================================
  protected nameTag!: Text;

  // ============================================
  // Damage Indicator (world-positioned health bar)
  // ============================================
  protected damageIndicator?: Container;
  protected damageBarBg?: Graphics;
  protected damageBarFill?: Graphics;
  protected damageHideDelayTween?: gsap.core.Tween;

  // Damage indicator config
  protected readonly damageBarWidth: number = 50;
  protected readonly damageBarHeight: number = 6;
  protected readonly nameTagOffsetY: number = -55;
  protected readonly damageIndicatorOffsetY: number = -70;

  // ============================================
  // Rotation Interpolation
  // ============================================
  protected targetRotation: number = 0;
  protected _angle: number = 0;

  public get angle(): number {
    return this._angle;
  }

  public set angle(value: number) {
    this._angle = value;
    this.rotation = degToRad(value);
  }

  // ============================================
  // Shield Hit Effect
  // ============================================
  protected shieldFlashGraphics?: Graphics;

  // ============================================
  // Debris (for explosion)
  // ============================================
  protected debris: ShipDebris[] = [];

  // ============================================
  // Abstract - subclasses must provide
  // ============================================

  /** Ship class ID for explosion debris generation */
  abstract get visibleShipClassId(): string;

  /** Ship color for debris */
  abstract get shipColor(): number;

  // ============================================
  // Name Tag Methods
  // ============================================

  /**
   * Create the name tag. Call in subclass constructor.
   */
  protected createNameTag(name: string, color: number): void {
    this.nameTag = new Text({
      text: name,
      style: {
        fontFamily: 'Arial, sans-serif',
        fontSize: 14,
        fontWeight: 'bold',
        fill: color,
        stroke: { color: 0x000000, width: 4 },
      },
    });
    this.nameTag.anchor.set(0.5);
    this.nameTag.zIndex = 100;
  }

  /**
   * Get name tag for parent to add to scene.
   */
  public getNameTag(): Text {
    return this.nameTag;
  }

  // ============================================
  // Damage Indicator Methods
  // ============================================

  /**
   * Create the damage indicator bar (initially hidden).
   * NOT added to container so it doesn't rotate with ship.
   * Call in subclass constructor.
   */
  protected createDamageIndicator(): void {
    this.damageIndicator = new Container();
    this.damageIndicator.visible = false;
    this.damageIndicator.zIndex = 100;

    // Background bar
    this.damageBarBg = new Graphics();
    this.damageBarBg.roundRect(
      -this.damageBarWidth / 2 - 1,
      -1,
      this.damageBarWidth + 2,
      this.damageBarHeight + 2,
      2
    );
    this.damageBarBg.fill({ color: 0x000000, alpha: 0.7 });
    this.damageIndicator.addChild(this.damageBarBg);

    // Fill bar (color set dynamically)
    this.damageBarFill = new Graphics();
    this.damageIndicator.addChild(this.damageBarFill);
  }

  /**
   * Get damage indicator for parent to add to scene.
   */
  public getDamageIndicator(): Container | undefined {
    return this.damageIndicator;
  }

  /**
   * Show damage indicator after taking damage.
   */
  public showDamageIndicator(isShieldDamage: boolean): void {
    if (!this.damageIndicator || !this.damageBarFill) return;

    // Calculate fill based on shield or hull
    let fillPercent: number;
    let fillColor: number;

    if (isShieldDamage && this.maxShield > 0) {
      fillPercent = Math.max(0, this.shield / this.maxShield);
      fillColor = 0x00aaff; // Blue for shield
    } else {
      fillPercent = Math.max(0, this.hull / this.maxHull);
      fillColor = 0xff4444; // Red for hull
    }

    // Update fill bar
    this.damageBarFill.clear();
    this.damageBarFill.roundRect(
      -this.damageBarWidth / 2,
      0,
      this.damageBarWidth * fillPercent,
      this.damageBarHeight,
      2
    );
    this.damageBarFill.fill({ color: fillColor, alpha: 1 });

    // Show indicator
    this.damageIndicator.visible = true;
    this.damageIndicator.alpha = 1;

    // Cancel existing timer
    if (this.damageHideDelayTween) {
      cancelDelay(this.damageHideDelayTween);
    }

    // Auto-hide after 2 seconds with fade
    this.damageHideDelayTween = delay(2000, () => {
      if (this.damageIndicator) {
        tween({
          targets: this.damageIndicator,
          alpha: 0,
          duration: 300,
          onComplete: () => {
            if (this.damageIndicator) this.damageIndicator.visible = false;
          },
        });
      }
    });
  }

  // ============================================
  // Rotation Interpolation
  // ============================================

  /**
   * Update rotation with smooth interpolation (frame-rate independent).
   * Uses exponential decay so the smoothing behaves identically at 60fps and 144fps.
   * @param lerpFactor - Base lerp factor calibrated for 60fps (~16.67ms frames)
   * @param deltaMs - Frame delta in milliseconds (pass to get frame-rate independence)
   */
  protected updateRotationInterpolation(lerpFactor: number = 0.15, deltaMs?: number): void {
    const currentRad = degToRad(this._angle);
    const targetRad = degToRad(this.targetRotation);
    const angleDiff = wrapAngle(targetRad - currentRad);

    // Frame-rate independent: convert per-frame factor to exponential decay
    // At 60fps (16.67ms), factor === lerpFactor (unchanged behavior)
    const baseDt = 16.667; // 60fps reference
    const dt = deltaMs !== undefined ? deltaMs : baseDt;
    const factor = 1 - Math.pow(1 - lerpFactor, dt / baseDt);

    this._angle = this._angle + radToDeg(angleDiff) * factor;
    this.rotation = degToRad(this._angle);
  }

  // ============================================
  // Position Updates
  // ============================================

  /**
   * Update name tag and damage indicator positions.
   * Call in update() loop after position updates.
   */
  protected updateNameAndIndicatorPositions(): void {
    this.nameTag.position.set(this.x, this.y + this.nameTagOffsetY);
    if (this.damageIndicator) {
      this.damageIndicator.position.set(this.x, this.y + this.damageIndicatorOffsetY);
    }
  }

  // ============================================
  // Hit Effects
  // ============================================

  /**
   * Show shield hit effect (arc flash).
   */
  public showShieldHit(hitAngle?: number): void {
    if (!this.shieldFlashGraphics) {
      this.shieldFlashGraphics = new Graphics();
      this.shieldFlashGraphics.zIndex = 50;
    }

    const g = this.shieldFlashGraphics;
    g.clear();
    g.position.set(this.x, this.y);

    // Draw shield arc at hit location — use actual shield radius if available
    const shieldRadius = (this as any).shieldRadius ?? (this as any)._shieldRadius ?? (this.collisionRadius + 10);
    const angle = hitAngle !== undefined ? hitAngle : Math.random() * Math.PI * 2;
    const arcHalf = Math.PI / 3; // 60 degree arc

    g.arc(0, 0, shieldRadius, angle - arcHalf, angle + arcHalf);
    g.stroke({ width: 3, color: 0x00aaff, alpha: 1 });

    // Add to parent if not already there
    if (this.parent && !g.parent) {
      this.parent.addChild(g);
    }

    g.alpha = 1;

    // Fade out
    tween({
      targets: g,
      alpha: 0,
      duration: 200,
      onComplete: () => {
        g.clear();
        g.alpha = 1;
      },
    });
  }

  /**
   * Get shield flash graphics for parent to add to scene.
   */
  public getShieldFlashGraphics(): Graphics | undefined {
    return this.shieldFlashGraphics;
  }

  /**
   * Show hull hit effect (sparks).
   */
  public showHullHit(hitAngle?: number): void {
    if (!this.parent) return;

    const angle = hitAngle !== undefined ? hitAngle : Math.random() * Math.PI * 2;
    const hitX = this.x + Math.cos(angle) * this.collisionRadius;
    const hitY = this.y + Math.sin(angle) * this.collisionRadius;

    // Create spark particles
    for (let i = 0; i < 8; i++) {
      const spark = new Graphics();
      spark.circle(0, 0, 2);
      spark.fill({ color: 0xff6600, alpha: 1 });
      spark.position.set(hitX, hitY);
      spark.zIndex = 10;
      this.parent.addChild(spark);

      const sparkAngle = angle + (Math.random() - 0.5) * 1.5;
      const speed = 80 + Math.random() * 60;
      const targetX = hitX + Math.cos(sparkAngle) * speed * 0.3;
      const targetY = hitY + Math.sin(sparkAngle) * speed * 0.3;

      tween({
        targets: spark,
        x: targetX,
        y: targetY,
        alpha: 0,
        duration: 200 + Math.random() * 100,
        ease: 'power2.out',
        onComplete: () => spark.destroy(),
      });
    }
  }

  // ============================================
  // Explosion / Destruction
  // ============================================

  /**
   * Public method to trigger explosion.
   */
  public explode(): void {
    if (this.isDestroyed) return;
    this.isDestroyed = true;
    this.onDestroy();
  }

  /**
   * Update debris animation. Call in update() when destroyed.
   * @returns Updated debris array (filtered for alive debris)
   */
  protected updateDebris(delta: number): void {
    this.debris = this.debris.filter(d => d.update(delta));
  }

  /**
   * Create ship explosion debris. Call from onDestroy() override.
   */
  protected createExplosionDebris(): void {
    if (this.parent) {
      createSVGExplosion(this.parent, this.x, this.y, this.rotation, this.visibleShipClassId, this.shipColor);
    }
  }

  /**
   * Check if a point (projectile) hits this ship.
   */
  public checkHit(x: number, y: number): boolean {
    if (this.isDestroyed) return false;
    const dx = x - this.x;
    const dy = y - this.y;
    const dist = Math.sqrt(dx * dx + dy * dy);
    return dist <= this.collisionRadius;
  }

  // ============================================
  // Cleanup
  // ============================================

  override destroy(): void {
    if (this.damageHideDelayTween) {
      cancelDelay(this.damageHideDelayTween);
    }
    this.shieldFlashGraphics?.destroy();
    this.nameTag?.destroy();
    this.damageIndicator?.destroy();
    this.debris.forEach(d => d.destroy());
    super.destroy();
  }
}
